import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Settings, BarChart2 } from 'lucide-react';
import { useSelectedMetrics, getCosClassName, getCpaClassName, useComponentMetrics, getMetricLabel, libelleConversions, libelleTauxConversion, infobulleEnteteConversion } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import EnteteAvecBulle from './EnteteAvecBulle';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';
import BoutonReglagesCampagnes from './campagnes/BoutonReglagesCampagnes';

import { calculateVariation, renderVariation } from '../utils/variationUtils';
import { colonnesVideoGoogleAds, cumulerVideo, TOTAUX_VIDEO_INITIAUX } from '../utils/metriquesVideo';
/**
 * Composant table modulaire des campagnes Google Ads
 * Optimisé avec React.memo et useMemo pour les performances
 */
const CampaignsTable = React.memo(({
  rawData,
  previousRawData = null,
  previousPeriodData = null,
  isVisible = true,
  onToggleVisibility,
  formatNumber,
  selectedCampaignFilter,
  setSelectedCampaignFilter,
  selectedCampaignTypeFilter,
  selectedAccount = null,
  filteredCampaignIds = null,
  compareEnabled = false,
  comparisonType = '',
  loadingComparison = false,
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'campaigns',
  /**
   * Ce que ce tableau énumère. « Campagnes » par défaut, pour que les dashboards existants
   * ne bougent pas ; Awin y met ses éditeurs, qui ne sont pas des campagnes et ne doivent pas
   * s'annoncer comme telles.
   */
  titre = 'Campagnes',
  titreColonne = 'Campagne',
  /**
   * Le compte dont on peut étiqueter les campagnes, et sa régie.
   *
   * **Deux valeurs scalaires, et non un objet.** Ce composant est `React.memo` : un littéral
   * `{{accountId, accountType}}` serait une nouvelle référence à chaque rendu du dashboard
   * parent et annulerait la mémoïsation sur les six points de montage — dans un fichier de
   * 41 000 lignes qui se rend en permanence, ce n'est pas un détail.
   *
   * `campagnesAccountId` attend le **`sourceId`** du compte, celui sous lequel les labels sont
   * indexés. Il ne peut pas être dérivé de la prop `selectedAccount` : côté Google Ads, ce n'est
   * pas le compte mais un objet reconstruit `{id, name}` sans `sourceId`.
   *
   * Absentes, le bouton ne s'affiche pas — c'est ainsi que le montage Awin des **éditeurs** s'en
   * exclut, sans liste noire sur `moduleId`.
   */
  campagnesAccountId = null,
  campagnesAccountType = null,
  campagnesToken = null,
  /**
   * Les deux totaux que ce tableau ne peut pas calculer lui-même.
   *
   * La couverture est un nombre de **personnes** : sommer celles de plusieurs campagnes compte
   * deux fois celles qui ont vu les deux. Seul Meta sait déduplier, et il le fait pour la
   * période et le compte entiers — c'est cette valeur qui arrive ici, via le résumé du
   * dashboard. La répétition, étant un ratio sur la couverture, suit la même règle.
   *
   * Scalaires et non un objet, pour la même raison que `campagnesAccountId` : ce composant est
   * `React.memo`, et un littéral recréé à chaque rendu parent annulerait la mémoïsation.
   * `null` quand le serveur n'a pas pu fournir la valeur.
   */
  totalCouverture = null,
  totalRepetition = null,
  /**
   * Ce que compte la métrique Conversions sur ce compte Meta, pour la nommer et pour que
   * l'engrenage de la modale ouvre son réglage. `null` sur les autres plateformes, dont la
   * métrique n'est pas configurable.
   */
  conversionConfig = null
}) => {
  // États locaux pour cette table
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Hook pour les métriques du composant (utilise moduleId pour supporter fbads_campaigns, etc.)
  const componentMetrics = useComponentMetrics(moduleId, selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricSelected = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Helper pour obtenir le label d'une métrique (avec labels personnalisés)
  const getLabel = useCallback((metricKey) => {
    /*
     * ⚠️ **`conv` et `taux_conv` ne se nomment pas dans une table figée.**
     *
     * Leur intitulé dépend de ce que l'utilisateur a choisi de compter sur ce compte Meta :
     * « Conversions (Réservation) », ou « Conversions (2 sources) ». Ce tableau l'omettait,
     * si bien que Campagnes annonçait « Conversions » tout court pendant que Plateformes, à
     * trois centimètres au-dessus, nommait la même mesure — deux colonnes du même écran
     * semblaient compter deux choses différentes.
     *
     * `conversionConfig` ne vaut quelque chose que sur Meta ; ailleurs la métrique n'est pas
     * configurable et le libellé reste celui de la table.
     */
    const personnalise = getMetricLabel(metricKey, moduleId, selectedAccount?.id || 'global');
    if (!conversionConfig) return personnalise;
    if (metricKey === 'conv') return libelleConversions(conversionConfig, null, 'fbads') || personnalise;
    if (metricKey === 'taux_conv') return libelleTauxConversion(conversionConfig, null, 'fbads') || personnalise;
    return personnalise;
  }, [moduleId, selectedAccount?.id, conversionConfig]);



  // Configuration du rendu des métriques (utilisé pour l'ordre dynamique)
  const metricConfig = useMemo(() => ({
    impressions: {
      defaultLabel: 'Impressions',
      sortKey: 'impressions',
      renderCell: (campaign, prevCampaign, hasComparison) => (
        <>
          {formatNumber(campaign.impressions || 0)}
          {hasComparison && renderVariation(calculateVariation(campaign.impressions, prevCampaign.impressions))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.impressions)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.impressions, previousTotals.impressions))}
        </>
      )
    },
    ctr: {
      defaultLabel: 'CTR',
      sortKey: 'ctr',
      renderCell: (campaign, prevCampaign, hasComparison) => (
        <>
          {formatNumber(campaign.ctr || 0, 2)}%
          {hasComparison && renderVariation(calculateVariation(campaign.ctr, prevCampaign.ctr))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.ctr, 2)}%</strong>
          {previousTotals && renderVariation(calculateVariation(totals.ctr, previousTotals.ctr))}
        </>
      )
    },
    clics: {
      defaultLabel: 'Clics',
      sortKey: 'clicks',
      renderCell: (campaign, prevCampaign, hasComparison) => (
        <>
          {formatNumber(campaign.clicks || 0)}
          {hasComparison && renderVariation(calculateVariation(campaign.clicks, prevCampaign.clicks))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.clicks)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.clicks, previousTotals.clicks))}
        </>
      )
    },
    cpc: {
      defaultLabel: 'CPC',
      sortKey: 'cpc',
      renderCell: (campaign, prevCampaign, hasComparison) => (
        <>
          €{formatNumber(campaign.cpc || 0, 2)}
          {hasComparison && renderVariation(calculateVariation(campaign.cpc, prevCampaign.cpc), true)}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>€{formatNumber(totals.cpc, 2)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cpc, previousTotals.cpc), true)}
        </>
      )
    },
    cout: {
      defaultLabel: 'Coût',
      sortKey: 'cost',
      renderCell: (campaign, prevCampaign, hasComparison) => (
        <>
          €{formatNumber(campaign.cost || 0, 2)}
          {hasComparison && renderVariation(calculateVariation(campaign.cost, prevCampaign.cost))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>€{formatNumber(totals.cost, 2)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cost, previousTotals.cost), true)}
        </>
      )
    },
    taux_conv: {
      defaultLabel: 'Taux conv.',
      sortKey: 'conversionRate',
      renderCell: (campaign, prevCampaign, hasComparison) => {
        const conversionRate = campaign.clicks > 0 ? (campaign.conversions / campaign.clicks * 100) : 0;
        const prevConversionRate = prevCampaign && prevCampaign.clicks > 0 ? (prevCampaign.conversions / prevCampaign.clicks * 100) : 0;
        return (
          <>
            {formatNumber(conversionRate, 2)}%
            {hasComparison && renderVariation(calculateVariation(conversionRate, prevConversionRate))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.conversionRate, 2)}%</strong>
          {previousTotals && renderVariation(calculateVariation(totals.conversionRate, previousTotals.conversionRate))}
        </>
      )
    },
    conv: {
      defaultLabel: 'Conv.',
      sortKey: 'conversions',
      renderCell: (campaign, prevCampaign, hasComparison) => (
        <>
          {formatNumber(campaign.conversions || 0, 1)}
          {hasComparison && renderVariation(calculateVariation(campaign.conversions, prevCampaign.conversions))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.conversions, 1)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.conversions, previousTotals.conversions))}
        </>
      )
    },
    /*
     * Les achats, distincts des conversions.
     *
     * Ce tableau sert aussi Google Ads, Microsoft, Pinterest et TikTok, dont `transactions`
     * n'est pas au catalogue (`COMPONENT_AVAILABLE_METRICS`) : la colonne ne s'y affiche donc
     * jamais. Sur Meta elle est proposée, et il lui faut une cellule — une métrique cochable
     * sans cellule pour l'afficher se coche sans rien produire.
     */
    transactions: {
      defaultLabel: 'Transactions',
      sortKey: 'transactions',
      renderCell: (campaign, prevCampaign, hasComparison) => (
        <>
          {formatNumber(campaign.transactions || 0, 1)}
          {hasComparison && renderVariation(calculateVariation(campaign.transactions || 0, prevCampaign.transactions || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.transactions || 0, 1)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.transactions || 0, previousTotals.transactions || 0))}
        </>
      )
    },
    cpa: {
      defaultLabel: 'CPA',
      sortKey: 'cpa',
      getClassName: (campaign) => {
        const cpa = campaign.conversions > 0 ? (campaign.cost / campaign.conversions) : 0;
        return getCpaClassName(cpa, selectedAccount?.id || 'global', moduleId);
      },
      getTotalClassName: (totals) => getCpaClassName(totals.cpa, selectedAccount?.id || 'global', moduleId),
      renderCell: (campaign, prevCampaign, hasComparison) => {
        const cpa = campaign.conversions > 0 ? (campaign.cost / campaign.conversions) : 0;
        const prevCpa = prevCampaign && prevCampaign.conversions > 0 ? (prevCampaign.cost / prevCampaign.conversions) : 0;
        return (
          <>
            {cpa >= 0 ? `€${formatNumber(cpa, 2)}` : '-'}
            {hasComparison && renderVariation(calculateVariation(cpa, prevCpa), true)}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{totals.cpa >= 0 ? `€${formatNumber(totals.cpa, 2)}` : '-'}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cpa, previousTotals.cpa), true)}
        </>
      )
    },
    valeur_conv: {
      defaultLabel: 'Valeur Conv.',
      sortKey: 'conversionsValue',
      renderCell: (campaign, prevCampaign, hasComparison) => (
        <>
          €{formatNumber(campaign.conversionsValue || 0, 2)}
          {hasComparison && renderVariation(calculateVariation(campaign.conversionsValue, prevCampaign.conversionsValue))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>€{formatNumber(totals.conversionsValue, 2)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.conversionsValue, previousTotals.conversionsValue))}
        </>
      )
    },
    /**
     * Vidéo : les ThruPlay, et leur part des impressions.
     *
     * Un tiret, et non zéro, sur une campagne sans vidéo : « 0 vue » se lirait comme une
     * vidéo que personne n'a regardée, là où il n'y a pas de vidéo du tout. Le total suit
     * la même règle — un ratio de sommes, jamais une moyenne de ratios.
     */
    videos_vues: {
      defaultLabel: 'Vidéos vues',
      sortKey: 'videoThruplay',
      renderCell: (campaign, prevCampaign, hasComparison) => (
        <>
          {campaign.videoThruplay == null ? '—' : formatNumber(campaign.videoThruplay)}
          {hasComparison && campaign.videoThruplay != null
            && renderVariation(calculateVariation(campaign.videoThruplay, prevCampaign?.videoThruplay))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{totals.videoThruplay > 0 ? formatNumber(totals.videoThruplay) : '—'}</strong>
          {previousTotals && totals.videoThruplay > 0
            && renderVariation(calculateVariation(totals.videoThruplay, previousTotals.videoThruplay))}
        </>
      )
    },
    taux_videos_vues: {
      defaultLabel: '% vidéos vues',
      sortKey: 'videoThruplay',
      renderCell: (campaign, prevCampaign, hasComparison) => {
        const taux = campaign.impressions > 0 && campaign.videoThruplay != null
          ? (campaign.videoThruplay / campaign.impressions) * 100
          : null;
        const prevTaux = prevCampaign?.impressions > 0 && prevCampaign?.videoThruplay != null
          ? (prevCampaign.videoThruplay / prevCampaign.impressions) * 100
          : 0;
        return (
          <>
            {taux === null ? '—' : `${formatNumber(taux, 2)}%`}
            {hasComparison && taux !== null && renderVariation(calculateVariation(taux, prevTaux))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{totals.tauxVideosVues === null ? '—' : `${formatNumber(totals.tauxVideosVues, 2)}%`}</strong>
          {previousTotals && totals.tauxVideosVues !== null
            && renderVariation(calculateVariation(totals.tauxVideosVues, previousTotals.tauxVideosVues))}
        </>
      )
    },
    /**
     * Interactions sociales : zéro est ici une information, pas une absence.
     *
     * Toute publication peut être commentée ou partagée — « 0 commentaire » dit quelque
     * chose de la créa, là où « 0 vue » sur une campagne sans vidéo ne dirait rien. D'où
     * un chiffre, et non un tiret.
     */
    commentaires: {
      defaultLabel: 'Commentaires',
      sortKey: 'comments',
      renderCell: (campaign, prevCampaign, hasComparison) => (
        <>
          {formatNumber(campaign.comments || 0)}
          {hasComparison && renderVariation(calculateVariation(campaign.comments || 0, prevCampaign?.comments || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.comments || 0)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.comments, previousTotals.comments))}
        </>
      )
    },
    enregistrements: {
      defaultLabel: 'Enregistrements',
      sortKey: 'saves',
      renderCell: (campaign, prevCampaign, hasComparison) => (
        <>
          {formatNumber(campaign.saves || 0)}
          {hasComparison && renderVariation(calculateVariation(campaign.saves || 0, prevCampaign?.saves || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.saves || 0)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.saves, previousTotals.saves))}
        </>
      )
    },
    partages: {
      defaultLabel: 'Partages',
      sortKey: 'shares',
      renderCell: (campaign, prevCampaign, hasComparison) => (
        <>
          {formatNumber(campaign.shares || 0)}
          {hasComparison && renderVariation(calculateVariation(campaign.shares || 0, prevCampaign?.shares || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.shares || 0)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.shares, previousTotals.shares))}
        </>
      )
    },
    jaime_facebook: {
      defaultLabel: "J'aime (Facebook)",
      sortKey: 'pageLikes',
      renderCell: (campaign, prevCampaign, hasComparison) => (
        <>
          {formatNumber(campaign.pageLikes || 0)}
          {hasComparison && renderVariation(calculateVariation(campaign.pageLikes || 0, prevCampaign?.pageLikes || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.pageLikes || 0)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.pageLikes, previousTotals.pageLikes))}
        </>
      )
    },
    cos: {
      defaultLabel: 'COS',
      sortKey: 'cos',
      getClassName: (campaign) => {
        const cos = (campaign.conversionsValue || 0) > 0 ? (campaign.cost / (campaign.conversionsValue || 1) * 100) : 0;
        return getCosClassName(cos, selectedAccount?.id || 'global', moduleId);
      },
      getTotalClassName: (totals) => getCosClassName(totals.cos, selectedAccount?.id || 'global', moduleId),
      renderCell: (campaign, prevCampaign, hasComparison) => {
        const cos = (campaign.conversionsValue || 0) > 0 ? (campaign.cost / (campaign.conversionsValue || 1) * 100) : 0;
        const prevCos = prevCampaign && (prevCampaign.conversionsValue || 0) > 0 ? (prevCampaign.cost / (prevCampaign.conversionsValue || 1) * 100) : 0;
        return (
          <>
            {formatNumber(cos, 1)}%
            {hasComparison && renderVariation(calculateVariation(cos, prevCos), true)}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.cos, 1)}%</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cos, previousTotals.cos), true)}
        </>
      )
    },
    panier_moyen: {
      defaultLabel: 'Panier moyen',
      sortKey: 'panierMoyen',
      renderCell: (campaign, prevCampaign, hasComparison) => {
        const panierMoyen = (campaign.conversions || 0) > 0 ? (campaign.conversionsValue || 0) / campaign.conversions : 0;
        const prevPanierMoyen = prevCampaign && (prevCampaign.conversions || 0) > 0 ? (prevCampaign.conversionsValue || 0) / prevCampaign.conversions : 0;
        return (
          <>
            €{formatNumber(panierMoyen, 2)}
            {hasComparison && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const panierMoyen = (totals.conversions || 0) > 0 ? (totals.conversionsValue || 0) / totals.conversions : 0;
        const prevPanierMoyen = previousTotals && (previousTotals.conversions || 0) > 0 ? (previousTotals.conversionsValue || 0) / previousTotals.conversions : 0;
        return (
          <>
            <strong>€{formatNumber(panierMoyen, 2)}</strong>
            {previousTotals && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      }
    },
    /**
     * Couverture et répétition — les deux métriques de personnes de Meta.
     *
     * **Par ligne, la valeur est juste** : Meta déduplique la couverture d'une campagne sur
     * toute la période. **Le total, lui, n'est pas une somme** : il vient du serveur, qui l'a
     * demandé à Meta pour le compte entier (voir `totauxNonSommables`). Quand il manque — liste
     * filtrée, ou appel refusé par l'API —, la case reste vide.
     */
    couverture: {
      defaultLabel: 'Couverture',
      sortKey: 'reach',
      renderCell: (campaign, prevCampaign, hasComparison) => (
        <>
          {formatNumber(campaign.reach || 0)}
          {hasComparison && renderVariation(calculateVariation(campaign.reach, prevCampaign.reach))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        totals.reach == null
          ? <span title="Meta ne déduplique la couverture que sur l'ensemble du compte : une liste filtrée n'a pas de total.">—</span>
          : <strong>{formatNumber(totals.reach)}</strong>
      )
    },
    repetition: {
      defaultLabel: 'Répétition',
      sortKey: 'frequency',
      renderCell: (campaign, prevCampaign, hasComparison) => (
        <>
          {formatNumber(campaign.frequency || 0, 2)}
          {hasComparison && renderVariation(calculateVariation(campaign.frequency, prevCampaign.frequency))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        totals.frequency == null
          ? <span title="La répétition est un ratio sur la couverture : elle ne se somme ni ne se moyenne, et suit le même périmètre.">—</span>
          : <strong>{formatNumber(totals.frequency, 2)}</strong>
      )
    },
    interactions: {
      defaultLabel: 'Interactions',
      sortKey: 'interactions',
      renderCell: (campaign, prevCampaign, hasComparison) => (
        <>
          {formatNumber(campaign.interactions || 0)}
          {hasComparison && renderVariation(calculateVariation(campaign.interactions, prevCampaign.interactions))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.interactions)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.interactions, previousTotals.interactions))}
        </>
      )
    },
    cout_par_interaction: {
      defaultLabel: 'Coût/Inter.',
      sortKey: 'cout_par_interaction',
      renderCell: (campaign, prevCampaign, hasComparison) => {
        const coutParInteraction = (campaign.interactions || 0) > 0 ? (campaign.cost / campaign.interactions) : 0;
        const prevCoutParInteraction = prevCampaign && (prevCampaign.interactions || 0) > 0 ? (prevCampaign.cost / prevCampaign.interactions) : 0;
        return (
          <>
            {coutParInteraction >= 0 ? `€${formatNumber(coutParInteraction, 2)}` : '-'}
            {hasComparison && renderVariation(calculateVariation(coutParInteraction, prevCoutParInteraction), true)}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{totals.cout_par_interaction >= 0 ? `€${formatNumber(totals.cout_par_interaction, 2)}` : '-'}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cout_par_interaction, previousTotals.cout_par_interaction), true)}
        </>
      )
    },
    reactions: {
      defaultLabel: 'Réactions',
      sortKey: 'reactions',
      renderCell: (campaign, prevCampaign, hasComparison) => (
        <>
          {formatNumber(campaign.reactions || 0)}
          {hasComparison && renderVariation(calculateVariation(campaign.reactions, prevCampaign.reactions))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{formatNumber(totals.reactions)}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.reactions, previousTotals.reactions))}
        </>
      )
    },
    cout_par_reaction: {
      defaultLabel: 'Coût/Réact.',
      sortKey: 'cout_par_reaction',
      renderCell: (campaign, prevCampaign, hasComparison) => {
        const coutParReaction = (campaign.reactions || 0) > 0 ? (campaign.cost / campaign.reactions) : 0;
        const prevCoutParReaction = prevCampaign && (prevCampaign.reactions || 0) > 0 ? (prevCampaign.cost / prevCampaign.reactions) : 0;
        return (
          <>
            {coutParReaction >= 0 ? `€${formatNumber(coutParReaction, 2)}` : '-'}
            {hasComparison && renderVariation(calculateVariation(coutParReaction, prevCoutParReaction), true)}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => (
        <>
          <strong>{totals.cout_par_reaction >= 0 ? `€${formatNumber(totals.cout_par_reaction, 2)}` : '-'}</strong>
          {previousTotals && renderVariation(calculateVariation(totals.cout_par_reaction, previousTotals.cout_par_reaction), true)}
        </>
      )
    },
    roas: {
      defaultLabel: 'ROAS',
      sortKey: 'roas',
      renderCell: (campaign, prevCampaign, hasComparison) => {
        const roas = (campaign.cost || 0) > 0 ? ((campaign.conversionsValue || 0) / campaign.cost) * 100 : 0;
        const prevRoas = prevCampaign && (prevCampaign.cost || 0) > 0 ? ((prevCampaign.conversionsValue || 0) / prevCampaign.cost) * 100 : 0;
        return (
          <>
            {roas >= 0 ? `${formatNumber(roas, 0)}%` : '-'}
            {hasComparison && renderVariation(calculateVariation(roas, prevRoas))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const roas = (totals.cost || 0) > 0 ? ((totals.conversionsValue || 0) / totals.cost) * 100 : 0;
        const prevRoas = previousTotals && (previousTotals.cost || 0) > 0 ? ((previousTotals.conversionsValue || 0) / previousTotals.cost) * 100 : 0;
        return (
          <>
            <strong>{roas >= 0 ? `${formatNumber(roas, 0)}%` : '-'}</strong>
            {previousTotals && renderVariation(calculateVariation(roas, prevRoas))}
          </>
        );
      }
    },
    roi: {
      defaultLabel: 'ROI',
      sortKey: 'roi',
      renderCell: (campaign, prevCampaign, hasComparison) => {
        const hasData = (campaign.cost || 0) > 0 && (campaign.conversionsValue || 0) > 0;
        const roi = hasData ? ((campaign.conversionsValue || 0) - campaign.cost) / campaign.cost : 0;
        const prevHasData = (prevCampaign?.cost || 0) > 0 && (prevCampaign?.conversionsValue || 0) > 0;
        const prevRoi = prevHasData ? ((prevCampaign.conversionsValue || 0) - prevCampaign.cost) / prevCampaign.cost : 0;
        return (
          <>
            {formatNumber(roi, 2)}
            {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const hasData = (totals.cost || 0) > 0 && (totals.conversionsValue || 0) > 0;
        const roi = hasData ? ((totals.conversionsValue || 0) - totals.cost) / totals.cost : 0;
        const prevHasData = (previousTotals?.cost || 0) > 0 && (previousTotals?.conversionsValue || 0) > 0;
        const prevRoi = prevHasData ? ((previousTotals.conversionsValue || 0) - previousTotals.cost) / previousTotals.cost : 0;
        return (
          <>
            <strong>{formatNumber(roi, 2)}</strong>
            {previousTotals && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      }
    },
    ...colonnesVideoGoogleAds({ formatNumber, renderVariation, calculateVariation })
  }), [formatNumber, selectedAccount?.id, calculateVariation, renderVariation]);

  // Extraction des données de campagnes de la période précédente
  const previousCampaignsData = useMemo(() => {
    if (!compareEnabled) return [];
    // Utiliser previousPeriodData.campaigns en priorité (nouvelle structure)
    if (previousPeriodData?.campaigns) {
      console.log('📊 [CampaignsTable] previousPeriodData.campaigns:', previousPeriodData.campaigns.length, 'campagnes');
      console.log('📊 [CampaignsTable] Exemple campagne précédente:', previousPeriodData.campaigns[0]);
      return previousPeriodData.campaigns;
    }
    // Fallback sur l'ancienne structure previousRawData
    if (previousRawData) {
      const accountData = Object.values(previousRawData).find(data => data.campaigns);
      return accountData?.campaigns || [];
    }
    return [];
  }, [previousRawData, previousPeriodData, compareEnabled]);

  // Récupérer les métriques sélectionnées avec mise à jour automatique
  const selectedMetrics = useSelectedMetrics();

  // Reset page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredCampaignIds, selectedCampaignFilter, selectedCampaignTypeFilter]);

  // Extraction des données de campagnes de base (avant filtre recherche)
  const baseCampaignsData = useMemo(() => {
    if (!rawData) return [];
    const accountData = Object.values(rawData).find(data => data.campaigns);
    let campaigns = accountData?.campaigns || [];

    // Filtrer par labels (filteredCampaignIds) si fourni
    if (filteredCampaignIds && Array.isArray(filteredCampaignIds) && filteredCampaignIds.length > 0) {
      campaigns = campaigns.filter(campaign => {
        const campaignId = (campaign.id || campaign.campaignId || '').toString();
        return filteredCampaignIds.includes(campaignId);
      });
    }

    // Filtrer pour n'afficher que la campagne sélectionnée si une campagne est filtrée
    if (selectedCampaignFilter) {
      campaigns = campaigns.filter(campaign =>
        campaign.id === selectedCampaignFilter.id ||
        campaign.campaignId === selectedCampaignFilter.id ||
        String(campaign.id) === String(selectedCampaignFilter.id) ||
        campaign.name === selectedCampaignFilter.name
      );
    }

    // Filtrer par type de campagne si un type est sélectionné
    if (selectedCampaignTypeFilter) {
      campaigns = campaigns.filter(campaign =>
        campaign.advertisingChannelType === selectedCampaignTypeFilter.type
      );
    }

    return campaigns;
  }, [rawData, selectedCampaignFilter, selectedCampaignTypeFilter, filteredCampaignIds]);

  // Filtrer par recherche textuelle (séparé pour éviter que le composant disparaisse)
  const campaignsData = useMemo(() => {
    if (!baseCampaignsData.length) return [];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      return baseCampaignsData.filter(campaign =>
        (campaign.name || '').toLowerCase().includes(query)
      );
    }

    return baseCampaignsData;
  }, [baseCampaignsData, searchQuery]);

  /**
   * Le total fourni porte-t-il sur ce que ce tableau affiche ?
   *
   * Les totaux non sommables viennent du serveur, qui les calcule **avec les mêmes filtres**
   * que ceux appliqués aux données : le filtre par label et le filtre par campagne sont donc
   * déjà pris en compte, et le total leur correspond.
   *
   * Restent les deux restrictions que le serveur ignore, parce qu'elles n'existent que dans
   * ce composant : le filtre par type de campagne et la recherche textuelle. Elles rétrécissent
   * la liste sans rétrécir le total, qui ne lui correspondrait donc plus — la case reste vide
   * plutôt que d'annoncer un chiffre portant sur autre chose.
   */
  const perimetreComplet = useMemo(() => (
    !selectedCampaignTypeFilter && !searchQuery.trim()
  ), [selectedCampaignTypeFilter, searchQuery]);

  // Fonction de tri memoized
  const sortedCampaigns = useMemo(() => {
    if (!campaignsData.length) return [];

    const sorted = [...campaignsData];
    sorted.sort((a, b) => {
      let aValue, bValue;
      
      // Calcul spécial pour CPA
      if (sortConfig.key === 'cpa') {
        aValue = a.conversions > 0 ? (a.cost / a.conversions) : Number.MAX_VALUE;
        bValue = b.conversions > 0 ? (b.cost / b.conversions) : Number.MAX_VALUE;
      } else {
        aValue = a[sortConfig.key] || 0;
        bValue = b[sortConfig.key] || 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [campaignsData, sortConfig]);

  // Calcul des totaux memoized
  const totals = useMemo(() => {
    if (!campaignsData.length) return {};

    const baseTotals = campaignsData.reduce((acc, campaign) => ({
      impressions: acc.impressions + (campaign.impressions || 0),
      clicks: acc.clicks + (campaign.clicks || 0),
      cost: acc.cost + (campaign.cost || 0),
      conversions: acc.conversions + (campaign.conversions || 0),
      conversionsValue: acc.conversionsValue + (campaign.conversionsValue || 0),
      transactions: acc.transactions + (campaign.transactions || 0),
      interactions: acc.interactions + (campaign.interactions || 0),
      reactions: acc.reactions + (campaign.reactions || 0),
      videoThruplay: acc.videoThruplay + (campaign.videoThruplay || 0),
      comments: acc.comments + (campaign.comments || 0),
      saves: acc.saves + (campaign.saves || 0),
      shares: acc.shares + (campaign.shares || 0),
      pageLikes: acc.pageLikes + (campaign.pageLikes || 0),
      ...cumulerVideo(acc, campaign)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, transactions: 0, interactions: 0, reactions: 0, videoThruplay: 0, comments: 0, saves: 0, shares: 0, pageLikes: 0, ...TOTAUX_VIDEO_INITIAUX });
    
    // Debug pour vérifier les conversions
    
    // Calculer les métriques dérivées
    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0,
      cout_par_interaction: baseTotals.interactions > 0 ? (baseTotals.cost / baseTotals.interactions) : 0,
      cout_par_reaction: baseTotals.reactions > 0 ? (baseTotals.cost / baseTotals.reactions) : 0,
      // `null` et non zéro sans vidéo : un taux de vue nul se lirait comme un échec là où
      // il n'y a rien à mesurer.
      tauxVideosVues: baseTotals.videoThruplay > 0 && baseTotals.impressions > 0
        ? (baseTotals.videoThruplay / baseTotals.impressions) * 100
        : null,

      /**
       * Couverture et répétition : reprises telles quelles, **jamais sommées**.
       *
       * Et seulement si la liste affichée est le compte entier. Dès qu'un filtre la rétrécit —
       * label, campagne, type, recherche —, le total dédupliqué du compte ne correspond plus à
       * ce qu'on voit, et Meta ne sait pas le recalculer pour un sous-ensemble arbitraire depuis
       * cet écran. La case reste alors vide : un chiffre juste pour un autre périmètre est plus
       * trompeur qu'une case vide.
       */
      reach: perimetreComplet ? totalCouverture : null,
      frequency: perimetreComplet ? totalRepetition : null
    };
  }, [campaignsData, perimetreComplet, totalCouverture, totalRepetition]);

  // Calcul des totaux précédents memoized
  const previousTotals = useMemo(() => {
    if (!previousCampaignsData.length || !compareEnabled) return null;

    const baseTotals = previousCampaignsData.reduce((acc, campaign) => ({
      impressions: acc.impressions + (campaign.impressions || 0),
      clicks: acc.clicks + (campaign.clicks || 0),
      cost: acc.cost + (campaign.cost || 0),
      conversions: acc.conversions + (campaign.conversions || 0),
      conversionsValue: acc.conversionsValue + (campaign.conversionsValue || 0),
      transactions: acc.transactions + (campaign.transactions || 0),
      interactions: acc.interactions + (campaign.interactions || 0),
      reactions: acc.reactions + (campaign.reactions || 0),
      videoThruplay: acc.videoThruplay + (campaign.videoThruplay || 0),
      comments: acc.comments + (campaign.comments || 0),
      saves: acc.saves + (campaign.saves || 0),
      shares: acc.shares + (campaign.shares || 0),
      pageLikes: acc.pageLikes + (campaign.pageLikes || 0),
      ...cumulerVideo(acc, campaign)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, transactions: 0, interactions: 0, reactions: 0, videoThruplay: 0, comments: 0, saves: 0, shares: 0, pageLikes: 0, ...TOTAUX_VIDEO_INITIAUX });

    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0,
      cout_par_interaction: baseTotals.interactions > 0 ? (baseTotals.cost / baseTotals.interactions) : 0,
      cout_par_reaction: baseTotals.reactions > 0 ? (baseTotals.cost / baseTotals.reactions) : 0,
      // `null` et non zéro sans vidéo : un taux de vue nul se lirait comme un échec là où
      // il n'y a rien à mesurer.
      tauxVideosVues: baseTotals.videoThruplay > 0 && baseTotals.impressions > 0
        ? (baseTotals.videoThruplay / baseTotals.impressions) * 100
        : null
    };
  }, [previousCampaignsData, compareEnabled]);

  // Configuration de pagination
  const itemsPerPage = 10;
  const totalCampaigns = sortedCampaigns.length;
  const totalPages = Math.ceil(totalCampaigns / itemsPerPage);

  // Réinitialiser la page quand les données changent
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Données paginées memoized
  const paginatedCampaigns = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalCampaigns);
    return sortedCampaigns.slice(startIndex, endIndex);
  }, [sortedCampaigns, currentPage, itemsPerPage, totalCampaigns]);

  // Handler de tri memoized
  const handleSort = useCallback((key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Réinitialiser à la première page
  }, [sortConfig]);

  // Handler de pagination memoized
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, newPage)));
  }, [totalPages]);

  // Ne pas rendre si pas de données de base (pas de campagnes du tout)
  if (!baseCampaignsData.length) return null;

  // Pour les clients : masquer complètement si le module est fermé
  if (userRole === 'CLIENT' && !isVisible) {
    return null;
  }

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalCampaigns);

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><BarChart2 size={16} />&nbsp;{titre}</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <BoutonReglagesCampagnes
            accountId={campagnesAccountId}
            accountType={campagnesAccountType}
            nomCompte={selectedAccount?.name || selectedAccount?.sourceName}
            userRole={userRole}
            token={campagnesToken}
          />
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title={titre}
            />
          ) : (
            <button
              onClick={onToggleVisibility}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <>
          {/* Indicateur de chargement des données de comparaison */}
          {compareEnabled && loadingComparison && (
            <div className="loading-message" style={{ textAlign: 'center', padding: '20px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 10px' }}></div>
              Chargement des données de comparaison...
            </div>
          )}
          {campaignsData.length > 0 && (!compareEnabled || !loadingComparison) && (
          <div className="campaigns-table-wrapper">
            <table 
              key={selectedMetrics.filter(m => m.selected).map(m => m.key).join(',')} 
              className="campaigns-table" 
              style={{ 
                width: '100%', 
                tableLayout: 'auto', 
                borderCollapse: 'collapse'
              }}>
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} className="sortable">
                    {titreColonne} {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  {/* Rendu dynamique des colonnes selon l'ordre configuré */}
                  {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                    const config = metricConfig[metricKey];
                    return (
                      <EnteteAvecBulle key={metricKey} onClick={() => handleSort(config.sortKey)} className="sortable numeric"
                        texte={infobulleEnteteConversion(metricKey, conversionConfig)}>
                        {getLabel(metricKey) || config.defaultLabel} {sortConfig.key === config.sortKey && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </EnteteAvecBulle>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {paginatedCampaigns.map((campaign, index) => {
                  // Trouver la campagne correspondante dans les données de la période précédente
                  // Utiliser String() pour normaliser les comparaisons d'ID
                  const prevCampaign = compareEnabled && previousCampaignsData.length > 0
                    ? previousCampaignsData.find(p =>
                        String(p.id) === String(campaign.id) ||
                        String(p.campaignId) === String(campaign.campaignId) ||
                        String(p.id) === String(campaign.campaignId) ||
                        String(p.campaignId) === String(campaign.id) ||
                        p.name === campaign.name
                      )
                    : null;

                  // Debug: log si on ne trouve pas de correspondance
                  if (compareEnabled && previousCampaignsData.length > 0 && !prevCampaign && index === 0) {
                    console.log('[CampaignsTable] Pas de correspondance pour campagne:', {
                      id: campaign.id,
                      campaignId: campaign.campaignId,
                      name: campaign.name
                    });
                    console.log('[CampaignsTable] Campagnes precedentes disponibles:', previousCampaignsData.slice(0, 3).map(p => ({
                      id: p.id,
                      campaignId: p.campaignId,
                      name: p.name
                    })));
                  }

                  const hasComparison = compareEnabled && prevCampaign;

                  return (
                    <tr
                      key={campaign.id || index}
                      onClick={() => setSelectedCampaignFilter(campaign)}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: selectedCampaignFilter?.id === campaign.id ? '#e3f2fd' : 'inherit'
                      }}
                      title="Cliquer pour filtrer tous les composants par cette campagne"
                    >
                      <td className="campaign-name" style={{ display: 'flex', alignItems: 'center' }}>
                        {(() => {
                          // isDelivering prend en compte le statut ET les dates de fin
                          // Disponible pour Meta Ads et Google Ads
                          if (campaign.isDelivering === undefined) return null;
                          return (
                            <span
                              style={{
                                display: 'inline-block',
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: campaign.isDelivering ? '#22c55e' : '#ef4444',
                                marginRight: '8px',
                                flexShrink: 0
                              }}
                              title={campaign.isDelivering ? 'Active' : 'Inactive'}
                            />
                          );
                        })()}
                        <span>{campaign.name}</span>
                      </td>
                      {/* Rendu dynamique des cellules selon l'ordre configuré */}
                      {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                        const config = metricConfig[metricKey];
                        const className = config.getClassName ? `numeric ${config.getClassName(campaign)}` : 'numeric';
                        return (
                          <td key={metricKey} className={className}>
                            {config.renderCell(campaign, prevCampaign || {}, hasComparison)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                <tr className="totals-row">
                  <td className="campaign-name"><strong>TOTAL</strong></td>
                  {/* Rendu dynamique des totaux selon l'ordre configuré */}
                  {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                    const config = metricConfig[metricKey];
                    const className = config.getTotalClassName ? `numeric ${config.getTotalClassName(totals)}` : 'numeric';
                    return (
                      <td key={metricKey} className={className}>
                        {config.renderTotal(totals, previousTotals)}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
          )}

          {/* Contrôles de pagination */}
          {campaignsData.length > 0 && totalPages > 1 && (
            <div className="pagination-controls">
              <div className="pagination-info">
                <span>
                  Affichage {startIndex + 1}-{Math.min(endIndex, sortedCampaigns.length)} sur {sortedCampaigns.length} campagnes
                </span>
              </div>
              <div className="pagination-buttons">
                <button 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  ← Précédent
                </button>
                <span className="page-numbers">
                  Page {currentPage} sur {totalPages}
                </span>
                <button 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Suivant →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId={moduleId}
        accountId={selectedAccount?.id || 'global'}
        componentTitle={moduleId.startsWith('fbads_') ? 'Campagnes Facebook Ads' : titre}
        /*
         * La définition de la métrique Conversions se règle dans l'engrenage de la métrique,
         * comme sur le dashboard Google Analytics. `conversionConfig` ne vaut quelque chose que
         * sur Meta : ailleurs la métrique n'est pas configurable, `METRICS_WITH_ADVANCED_OPTIONS`
         * ne déclare rien pour ces composants, et la modale n'affiche donc pas le réglage.
         *
         * Le rechargement est nécessaire : les séries sont calculées côté serveur avec
         * l'ancienne définition, et l'écran afficherait sinon le nouveau nom au-dessus des
         * anciens chiffres.
         */
        token={campagnesToken}
        propertyId={selectedAccount?.id}
        currentAdvancedConfig={conversionConfig}
        onAdvancedConfigSave={() => window.location.reload()}
      />
    </div>
  );
});

CampaignsTable.displayName = 'CampaignsTable';

export default CampaignsTable;